import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Button,
} from "@mui/material";
import { useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import { ClinicianWithPricing } from "../../../api/types/api.types";
import { RoleGuard } from "../../../components/RBAC";

interface ClinicianPricingRowProps {
  clinician: ClinicianWithPricing;
  onEditService: (
    clinicianId: number,
    serviceId: number,
    mode: string,
    config: any
  ) => void;
  onGlobalUpdate: (clinicianId: number, clinicianName: string) => void;
}

export const ClinicianPricingRow = ({
  clinician,
  onEditService,
  onGlobalUpdate,
}: ClinicianPricingRowProps) => {
  const [open, setOpen] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: "SAR",
    }).format(amount);
  };

  const { clinician: clinicianData, servicePricing, summary } = clinician;

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={clinicianData.imageUrl || ""}
              alt={clinicianData.fullName}
            >
              {clinicianData.fullName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="600">
                {clinicianData.fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {clinicianData.email}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>{clinicianData.designation || "N/A"}</TableCell>
        <TableCell>{clinicianData.experience} years</TableCell>
        <TableCell>
          <Chip
            label={`${summary.totalServices} Services`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={`${summary.totalPricingConfigs} Configs`}
            color={summary.hasPricing ? "success" : "default"}
            size="small"
          />
        </TableCell>
        <TableCell>
          <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                onGlobalUpdate(clinicianData.id, clinicianData.fullName)
              }
            >
              Global Update
            </Button>
          </RoleGuard>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Service Pricing Details
              </Typography>
              {servicePricing.length === 0 ? (
                <Typography color="text.secondary">
                  No pricing configured for this clinician
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Audio Price</TableCell>
                      <TableCell>Video Price</TableCell>
                      <TableCell>Home Price</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {servicePricing.map((service) => (
                      <TableRow key={service.serviceId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {service.serviceName}
                          </Typography>
                          {service.serviceArabicName && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {service.serviceArabicName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {service.pricing.AUDIO ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography>
                                {formatCurrency(service.pricing.AUDIO.price)}
                              </Typography>
                              <Chip
                                label={
                                  service.pricing.AUDIO.isActive
                                    ? "Active"
                                    : "Inactive"
                                }
                                color={
                                  service.pricing.AUDIO.isActive
                                    ? "success"
                                    : "default"
                                }
                                size="small"
                              />
                              <RoleGuard
                                allowedRoles={["SUPER_ADMIN", "ADMIN"]}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    onEditService(
                                      clinicianData.id,
                                      service.serviceId,
                                      "AUDIO",
                                      service.pricing.AUDIO
                                    )
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </RoleGuard>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">
                              Not configured
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {service.pricing.VIDEO ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography>
                                {formatCurrency(service.pricing.VIDEO.price)}
                              </Typography>
                              <Chip
                                label={
                                  service.pricing.VIDEO.isActive
                                    ? "Active"
                                    : "Inactive"
                                }
                                color={
                                  service.pricing.VIDEO.isActive
                                    ? "success"
                                    : "default"
                                }
                                size="small"
                              />
                              <RoleGuard
                                allowedRoles={["SUPER_ADMIN", "ADMIN"]}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    onEditService(
                                      clinicianData.id,
                                      service.serviceId,
                                      "VIDEO",
                                      service.pricing.VIDEO
                                    )
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </RoleGuard>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">
                              Not configured
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {service.pricing.HOME ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography>
                                {formatCurrency(service.pricing.HOME.price)}
                              </Typography>
                              <Chip
                                label={
                                  service.pricing.HOME.isActive
                                    ? "Active"
                                    : "Inactive"
                                }
                                color={
                                  service.pricing.HOME.isActive
                                    ? "success"
                                    : "default"
                                }
                                size="small"
                              />
                              <RoleGuard
                                allowedRoles={["SUPER_ADMIN", "ADMIN"]}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    onEditService(
                                      clinicianData.id,
                                      service.serviceId,
                                      "HOME",
                                      service.pricing.HOME
                                    )
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </RoleGuard>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">
                              Not configured
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {service.serviceId}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
